import { computed, ref, useRoute, watch } from '@nuxtjs/composition-api';
import launchDarkly from '@bambeehr/vue-launch-darkly';
import FeatureFlags from '@/constants/FeatureFlags';
import isCompanyAdmin from '@/helpers/accessControl/isCompanyAdmin';
import useCurrentCompany from '@/hooks/useCurrentCompany';
import bam from '@/lib/bam';
import {
  getFromLocalStorage,
  saveInLocalStorage,
} from '@/utils/localStorage.js';
import migrationBanner from './content';

export const DISMISS_KEY_PREFIX = 'migration-banner-dismissed';

export const MigrationBannerEvents = Object.freeze({
  SHOWN: 'migration-banner-shown',
  CTA_CLICKED: 'migration-banner-cta-clicked',
  DISMISSED: 'migration-banner-dismissed',
});

// One impression per page load, not one per re-render. Module scope rather than composable scope
// so it still holds if the composable is ever mounted from a second place.
let hasTrackedImpression = false;

/**
 * Storage key for one company's dismissal of one version of the banner.
 *
 * Both parts matter. The company id keeps a dismissal from leaking between accounts on a shared
 * browser — a real risk here, since HR Managers sign into customer accounts. The content version
 * makes dismissal revocable: bump `version` in content.ts and every previously dismissed company
 * sees the banner again, which is the lever for the run-up to a wave's move date.
 */
export const buildDismissKey = (companyId: string, version: string): string =>
  `${DISMISS_KEY_PREFIX}-${version}-${companyId}`;

/**
 * Decides who sees the Bambee 2.0 migration banner (BIO-1908), and reports on it.
 *
 * Audience lives in LaunchDarkly, not here. The `migration-banner` flag carries a targeting rule
 * on the `companyId` custom attribute (set in plugins/launch-darkly.js), so the wave list is a
 * segment edit rather than a deploy — and waves 2-4 ship without touching this file.
 *
 * The role check is deliberately duplicated in code rather than left to the LD rule. Flags
 * evaluate against an anonymous context until `identify` resolves, so there is a brief window
 * where a flag can read true before the company is known. Employees must never see this, so the
 * role test is the authority and the flag only narrows it further.
 *
 * `?migrationBanner=1` overrides the *flag* for local review, mirroring CountdownBanner's existing
 * `?immediate-request=true` gate. It deliberately overrides neither the role check nor dismissal,
 * so both remain testable with the override switched on.
 *
 * Dismissal is hand-rolled rather than routed through hooks/useState. useState binds its reactive
 * state to the storage key resolved at call time, and `companyId` is still empty when the layout's
 * setup runs — so the dismissal would be written under a company-less key. Reading the key lazily
 * avoids that ordering problem entirely.
 */
const useMigrationBanner = () => {
  const route = useRoute();
  const { companyId } = useCurrentCompany();

  // Local review override. Bypasses LaunchDarkly only.
  const isOverridden = computed(
    () => route.value?.query?.migrationBanner === '1'
  );

  // Wave membership, owned by the LaunchDarkly segment.
  const isInMigrationCohort = computed(
    () => !!launchDarkly.getFlags()?.[FeatureFlags.MIGRATION_BANNER]
  );

  // Company owner only. `UserRoles.USER` is the business owner; `ADMIN` is Bambee staff and
  // `EMPLOYEE` is a worker, neither of whom should be told their company is migrating.
  const isOwner = computed(() => isCompanyAdmin());

  const dismissKey = computed(() =>
    companyId.value
      ? buildDismissKey(companyId.value, migrationBanner.version)
      : ''
  );

  // localStorage is not reactive, so a write has to tell the computed to re-read.
  const dismissTick = ref(0);

  const isDismissed = computed(() => {
    // Touch the tick so dismissing re-evaluates this.
    // eslint-disable-next-line no-unused-expressions
    dismissTick.value;

    if (!dismissKey.value) {
      return false;
    }

    return getFromLocalStorage(dismissKey.value) === 'true';
  });

  const showMigrationBanner = computed(
    () =>
      (isInMigrationCohort.value || isOverridden.value) &&
      isOwner.value &&
      !isDismissed.value
  );

  // Common properties on every event, so impressions, clicks and dismissals can be joined against
  // the wave list by company and compared across content revisions.
  const eventContext = () => ({
    companyId: companyId.value,
    version: migrationBanner.version,
    // Distinguishes real cohort traffic from local review, so overridden sessions can be excluded.
    overridden: isOverridden.value,
  });

  const trackMigrationBannerCta = (): void => {
    bam.trackEvent(MigrationBannerEvents.CTA_CLICKED, {
      ...eventContext(),
      destination: migrationBanner.ctaUrl,
    });
  };

  const dismissMigrationBanner = (): void => {
    if (!dismissKey.value) {
      return;
    }

    saveInLocalStorage(dismissKey.value, 'true');
    dismissTick.value += 1;
    bam.trackEvent(MigrationBannerEvents.DISMISSED, eventContext());
  };

  // Fires when the banner actually becomes visible, not when the layout mounts — `companyId` and
  // the LD flags both resolve after setup, so an on-mount event would report the wrong thing.
  watch(
    showMigrationBanner,
    (isVisible) => {
      if (isVisible && !hasTrackedImpression) {
        hasTrackedImpression = true;
        bam.trackEvent(MigrationBannerEvents.SHOWN, eventContext());
      }
    },
    { immediate: true }
  );

  return {
    showMigrationBanner,
    dismissMigrationBanner,
    trackMigrationBannerCta,
    isInMigrationCohort,
    isOwner,
    isOverridden,
    isDismissed,
    dismissKey,
  };
};

export default useMigrationBanner;
